import { fetchAuthSession } from 'aws-amplify/auth'

const BASE_URL = (import.meta.env.VITE_API_BASE_URL || '/api').replace(/\/$/, '')
const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === 'true'

async function authHeader(): Promise<HeadersInit> {
  if (AUTH_DISABLED) return {}
  try {
    const session = await fetchAuthSession()
    const token = session.tokens?.idToken?.toString()
    return token ? { Authorization: `Bearer ${token}` } : {}
  } catch {
    return {}
  }
}

export async function apiGet<T>(path: string): Promise<T> {
  const headers = await authHeader()
  const cacheBustedPath = withCacheBuster(path)
  const res = await fetch(`${BASE_URL}${cacheBustedPath}`, {
    method: 'GET',
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      ...headers,
    },
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new ApiError(res.status, `${res.status} ${res.statusText}: ${text}`)
  }
  return (await res.json()) as T
}

function withCacheBuster(path: string): string {
  const separator = path.includes('?') ? '&' : '?'
  return `${path}${separator}_=${Date.now()}`
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const headers = await authHeader()
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new ApiError(res.status, `${res.status} ${res.statusText}: ${text}`)
  }
  return (await res.json()) as T
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// ---------------------------------------------------------------------------
// Resource shapes — mirror what the API handler returns.
// ---------------------------------------------------------------------------
export interface RunManifestFile {
  name: string
  run_key: string
  latest_key: string
  size_bytes: number
  rows?: number
}

export interface RunInfo {
  run_id: string
  status: 'pending' | 'running' | 'succeeded' | 'failed' | 'unknown'
  started_at: string | null
  finished_at: string | null
  backend?: string
  returncode?: number | null
  log_tail?: string | null
  error?: string | null
}

export interface RunManifest {
  run_id: string | null
  started_at: string | null
  finished_at?: string | null
  status: 'running' | 'succeeded' | 'failed' | 'unknown'
  git_sha?: string
  data_bucket?: string | null
  prefix?: string
  files: RunManifestFile[]
  error?: string
  /** Most recent parquet timestamp across all datasets (UTC ISO). */
  data_updated_at?: string | null
  /** A currently-executing run, if any (set in local mode). */
  in_flight?: RunInfo
}

export interface TeamSummaryRow {
  League: string
  Division: string
  Team: string
  Rating: number
  Win: number
  Loss: number
  'Most Regular Season Wins %': number
  'Fewest Regular Season Wins %': number
  'Playoff %': number
  'Division %': number
  'Pennant %': number
  'WS %': number
}

/** One row of inputs/team_mapping.parquet */
export interface TeamMappingRow {
  MLB_Name: string
  Mapping_Name: string
  League: string
  Division: string
  [k: string]: unknown
}

export interface CurrentRatingRow {
  Team: string
  'W-L': string
  Pyth: number
  Current: number
  '2026 Rating': number
  'PS Rating': number
  Total: number
  'Total Rk': number
  SOS: number
  'SOS Rk': number
  rSOS: number
  'rSOS Rk': number
  'PS Weight': number
  Check: number
  [k: string]: unknown
}

export interface RemainingScheduleRow {
  GamePk?: number
  Date: string
  Away_Team: string
  Home_Team: string
  Status: string
  Scheduled_Time_UTC?: string
  [k: string]: unknown
}

export interface BoxScoreRow {
  GamePk?: number
  Date: string
  Away_Team: string
  Home_Team: string
  Away_Runs?: number
  Home_Runs?: number
  Status: string
  Scheduled_Time_UTC?: string
  [k: string]: unknown
}

export interface PsRatingHistoryEntry {
  change_id: string
  changed_at: string
  changed_by: string
  old_rating: number
  new_rating: number
  reason: string
}

export interface PsRatingRow {
  team: string
  baseline_rating: number
  effective_rating: number
  last_changed_at: string | null
  last_changed_by: string | null
  last_reason: string | null
  history: PsRatingHistoryEntry[]
}

export interface PsRatingAuditRow extends PsRatingHistoryEntry {
  team: string
}

export interface PsRatingAdjustmentInput {
  team: string
  new_rating: number
  reason?: string
}

export interface PsRatingLeagueBalance {
  league_sum: number
  league_mean: number
  target_sum: number
  tolerance: number
  ok: boolean
  note: string
}

export interface PsRatingResponse {
  rows: PsRatingRow[]
  balance?: PsRatingLeagueBalance
}

export interface PsRatingAuditResponse {
  rows: PsRatingAuditRow[]
}

export interface PsRatingSaveResponse {
  written: PsRatingAuditRow[]
  rows: PsRatingRow[]
  balance?: PsRatingLeagueBalance
}

/** One row of ratings/ratings_by_day.parquet */
export interface RatingsByDayRow extends CurrentRatingRow {
  snapshot_date: string
}

export interface DatasetEntry {
  name: string
  group: string
  description: string
  uri: string
  row_count: number | null
  columns: string[]
  available: boolean
}

export interface DatasetPage<T = Record<string, unknown>> {
  name: string
  group: string
  columns: string[]
  rows: T[]
  total: number
  limit: number | null
  offset: number
}

export interface DatasetSchema {
  name: string
  group: string
  uri: string
  columns: { name: string; type: string }[]
}

export const Api = {
  health: () => apiGet<{ ok: boolean; service: string; data_bucket: string; datasets: string[] }>('/health'),
  whoami: () => apiGet<{ email?: string; name?: string; sub?: string }>('/whoami'),
  latestRun: () => apiGet<RunManifest>('/runs/latest'),

  // Generic dataset access — single source of truth.
  listDatasets: () => apiGet<DatasetEntry[]>('/datasets'),
  getDataset: <T = Record<string, unknown>>(
    name: string,
    opts: {
      limit?: number
      offset?: number
      orderBy?: string
      fields?: string
      /** Calendar year filter (API parses dataset date column). */
      year?: number | null
      dateColumn?: string
    } = {},
  ) => {
    const params = new URLSearchParams()
    if (opts.limit !== undefined) params.set('limit', String(opts.limit))
    if (opts.offset !== undefined) params.set('offset', String(opts.offset))
    if (opts.orderBy !== undefined) params.set('order_by', opts.orderBy)
    if (opts.fields !== undefined && opts.fields !== '') params.set('fields', opts.fields)
    if (opts.year != null) params.set('year', String(opts.year))
    if (opts.dateColumn) params.set('date_column', opts.dateColumn)
    const qs = params.toString()
    return apiGet<DatasetPage<T>>(`/datasets/${encodeURIComponent(name)}${qs ? `?${qs}` : ''}`)
  },
  getDatasetSchema: (name: string) =>
    apiGet<DatasetSchema>(`/datasets/${encodeURIComponent(name)}/schema`),

  // Convenience aliases (backed by the generic endpoint server-side).
  ratings: () => apiGet<CurrentRatingRow[]>('/ratings'),
  standings: () => apiGet<TeamSummaryRow[]>('/standings'),
  winMatrix: () => apiGet<Record<string, number | string>[]>('/win-matrix'),
  remainingSchedule: () => apiGet<RemainingScheduleRow[]>('/schedule/remaining'),
  boxScores: () => apiGet<BoxScoreRow[]>('/schedule/box-scores'),
  psRatings: () => apiGet<PsRatingResponse>('/ps-ratings'),
  psRatingsAudit: () => apiGet<PsRatingAuditResponse>('/ps-ratings/audit'),
  savePsRatingAdjustments: (changes: PsRatingAdjustmentInput[]) =>
    apiPost<PsRatingSaveResponse>('/ps-ratings/adjustments', { changes }),

  // Pipeline trigger + status.
  triggerRun: () => apiPost<RunInfo>('/runs'),
  getRun: (runId: string) => apiGet<RunInfo>(`/runs/${encodeURIComponent(runId)}`),
}
